import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Lightning,
  ChartLineUp,
  TrendUp,
  ArrowUp,
  ArrowDown,
  CircleNotch,
  Play,
  Robot
} from "@phosphor-icons/react";

const BOT_META = {
  scalp: { icon: Lightning, color: "#ef4444", label: "Scalping", timeframe: "M1/M5", desc: "Breakout range + EMA 9/21 + RSI" },
  daytrade: { icon: ChartLineUp, color: "#f59e0b", label: "Day Trade", timeframe: "M15/H1", desc: "MACD + Support/Resistance + EMA 50" },
  swing: { icon: TrendUp, color: "#3b82f6", label: "Swing", timeframe: "H4/D1", desc: "EMA 50/200 + Fibonacci 0.618 + Pin Bar" },
};

export default function Bots() {
  const [configs, setConfigs] = useState([]);
  const [signals, setSignals] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bots/configs`);
      const data = await res.json();
      setConfigs(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bots/signals?limit=100`);
      const data = await res.json();
      setSignals(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchSignals();
    const interval = setInterval(fetchSignals, 30000);
    return () => clearInterval(interval);
  }, [fetchConfigs, fetchSignals]);

  const toggleBot = async (botId) => {
    try {
      const res = await fetch(`${API}/bots/toggle/${botId}`, { method: "POST" });
      const data = await res.json();
      toast.success(`Bot ${botId} ${data.enabled ? "active" : "desactive"}`);
      fetchConfigs();
    } catch (e) {
      toast.error("Erreur toggle bot");
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${API}/bots/scan`);
      const data = await res.json();
      toast.success(`Scan termine: ${data.signals_found} signaux trouves`);
      fetchSignals();
    } catch (e) {
      toast.error("Erreur scan");
    } finally {
      setScanning(false);
    }
  };

  const filteredSignals = activeTab === "all"
    ? signals
    : signals.filter(s => s.bot_id === activeTab);

  return (
    <div className="space-y-6" data-testid="bots-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900" data-testid="bots-title">
            Bots de Trading
          </h1>
          <p className="text-sm text-slate-500 mt-1">Execution automatique en paper trading</p>
        </div>
        <Button
          onClick={runScan}
          disabled={scanning}
          data-testid="scan-button"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {scanning ? <CircleNotch size={18} className="animate-spin mr-2" /> : <Play size={18} className="mr-2" />}
          {scanning ? "Scan en cours..." : "Lancer le scan"}
        </Button>
      </div>

      {/* Bot Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {configs.map((config) => {
          const meta = BOT_META[config.bot_id] || {};
          const Icon = meta.icon || Robot;
          const botSignals = signals.filter(s => s.bot_id === config.bot_id);
          const lastSignal = botSignals[0];

          return (
            <Card key={config.bot_id} data-testid={`bot-card-${config.bot_id}`}
              className={`relative overflow-hidden ${config.enabled ? 'ring-2' : ''}`}
              style={{ borderColor: config.enabled ? meta.color : undefined, ringColor: config.enabled ? meta.color : undefined }}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}15` }}>
                      <Icon size={18} weight="bold" style={{ color: meta.color }} />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold">{config.name}</CardTitle>
                      <p className="text-xs text-slate-400">{meta.timeframe}</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={() => toggleBot(config.bot_id)}
                    data-testid={`bot-toggle-${config.bot_id}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <p className="text-xs text-slate-500">{meta.desc}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Signaux:</span>
                  <span className="font-semibold">{botSignals.length}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Position:</span>
                  <span className="font-semibold">{config.position_size_pct}% du capital</span>
                </div>
                {config.bot_id === "scalp" && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">Actifs:</span>
                    <span className="font-semibold text-red-500">DAX uniquement</span>
                  </div>
                )}
                {lastSignal && (
                  <div className={`p-2 rounded-lg text-xs ${lastSignal.signal_type === 'BUY' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    <div className="flex items-center gap-1 font-semibold">
                      {lastSignal.signal_type === 'BUY' ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                      {lastSignal.signal_type} {lastSignal.symbol} @ ${lastSignal.price?.toLocaleString()}
                    </div>
                    <p className="mt-1 opacity-75">{lastSignal.reason}</p>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${config.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                  <span className="text-xs text-slate-400">{config.enabled ? "Actif - scan toutes les 5 min" : "Desactive"}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Signals Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Signaux detectes</CardTitle>
            <div className="flex gap-1">
              {["all", "scalp", "daytrade", "swing"].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  data-testid={`tab-${tab}`}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    activeTab === tab
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab === "all" ? "Tous" : BOT_META[tab]?.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredSignals.length === 0 ? (
            <div className="text-center py-12 text-slate-400" data-testid="no-signals">
              <Robot size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">Aucun signal detecte</p>
              <p className="text-sm mt-1">Activez un bot et lancez le scan</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="signals-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Bot</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Symbole</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Signal</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">Prix</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">SL</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">TP1</th>
                    <th className="text-right py-2 px-2 text-slate-400 font-medium text-xs">TP2</th>
                    <th className="text-left py-2 px-2 text-slate-400 font-medium text-xs">Raison</th>
                    <th className="text-center py-2 px-2 text-slate-400 font-medium text-xs">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSignals.map((sig) => {
                    const meta = BOT_META[sig.bot_id] || {};
                    return (
                      <tr key={sig.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`signal-row-${sig.id}`}>
                        <td className="py-2 px-2">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2 px-2 font-semibold">{sig.symbol}</td>
                        <td className="py-2 px-2">
                          <span className={`flex items-center gap-1 text-xs font-bold ${sig.signal_type === 'BUY' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {sig.signal_type === 'BUY' ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
                            {sig.signal_type}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">${sig.price?.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-red-500">${sig.sl?.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-emerald-500">${sig.tp1?.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-emerald-500">${sig.tp2?.toLocaleString()}</td>
                        <td className="py-2 px-2 text-xs text-slate-500 max-w-[200px] truncate">{sig.reason}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            sig.status === 'executed' ? 'bg-emerald-100 text-emerald-700' :
                            sig.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {sig.status === 'executed' ? 'Execute' : sig.status === 'expired' ? 'Expire' : 'Nouveau'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
